import React from 'react';
import { Search, Filter, RotateCcw, MapPin, Building2, DollarSign } from 'lucide-react';

const PropertyFilters = ({ filters, communes, onFilterChange, onFilterReset }) => {
  const statuses = [
    { value: 'available', label: 'Disponible' },
    { value: 'reserved', label: 'Réservé' },
    { value: 'sold', label: 'Vendu' },
    { value: 'withdrawn', label: 'Retiré' }
  ];

  const propertyTypes = [
    { value: 'apartment', label: 'Appartement' },
    { value: 'house', label: 'Maison' },
    { value: 'villa', label: 'Villa' },
    { value: 'land', label: 'Terrain' },
    { value: 'commercial', label: 'Commercial' }
  ];

  const sortOptions = [
    { value: 'created_at', label: 'Date de création' },
    { value: 'title', label: 'Titre' },
    { value: 'price', label: 'Prix' },
    { value: 'status', label: 'Statut' },
    { value: 'property_type', label: 'Type' }
  ];

  const hasActiveFilters = Object.values(filters)?.some(value => 
    value !== '' && value !== 'created_at' && value !== 'desc'
  );

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
          <Filter className="h-5 w-5 text-muted-foreground" />
          Filtres et recherche
        </h3>
        
        {hasActiveFilters && (
          <button
            onClick={onFilterReset}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 rounded-md transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Réinitialiser
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Search */}
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-2">
            Recherche globale
          </label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Titre, adresse, description, agent..."
              value={filters?.search}
              onChange={(e) => onFilterChange?.('search', e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Commune Filter */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            Commune
          </label>
          <select
            value={filters?.commune}
            onChange={(e) => onFilterChange?.('commune', e?.target?.value)}
            className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-transparent text-sm"
          >
            <option value="">Toutes les communes</option>
            {communes?.map(commune => (
              <option key={commune?.id} value={commune?.id}>
                {commune?.name} - {commune?.district}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Statut
          </label>
          <select
            value={filters?.status}
            onChange={(e) => onFilterChange?.('status', e?.target?.value)}
            className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-transparent text-sm"
          >
            <option value="">Tous les statuts</option>
            {statuses?.map(status => (
              <option key={status?.value} value={status?.value}>{status?.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {/* Type Filter */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-1">
            <Building2 className="h-4 w-4" />
            Type
          </label>
          <select
            value={filters?.type}
            onChange={(e) => onFilterChange?.('type', e?.target?.value)}
            className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-transparent text-sm"
          >
            <option value="">Tous les types</option>
            {propertyTypes?.map(type => (
              <option key={type?.value} value={type?.value}>{type?.label}</option>
            ))}
          </select>
        </div>

        {/* Price Range */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-1">
            <DollarSign className="h-4 w-4" />
            Prix min (FCFA)
          </label>
          <input
            type="number"
            placeholder="Prix minimum"
            value={filters?.priceMin}
            onChange={(e) => onFilterChange?.('priceMin', e?.target?.value)}
            className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-transparent text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-1">
            <DollarSign className="h-4 w-4" />
            Prix max (FCFA)
          </label>
          <input
            type="number"
            placeholder="Prix maximum"
            value={filters?.priceMax}
            onChange={(e) => onFilterChange?.('priceMax', e?.target?.value)}
            className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-transparent text-sm"
          />
        </div>

        {/* Sort Options */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Trier par
          </label>
          <div className="flex gap-2">
            <select
              value={filters?.sortBy}
              onChange={(e) => onFilterChange?.('sortBy', e?.target?.value)}
              className="flex-1 px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-transparent text-sm"
            >
              {sortOptions?.map(option => (
                <option key={option?.value} value={option?.value}>{option?.label}</option>
              ))}
            </select>
            
            <select
              value={filters?.sortOrder}
              onChange={(e) => onFilterChange?.('sortOrder', e?.target?.value)}
              className="px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-transparent text-sm"
            >
              <option value="asc">↑ Asc</option>
              <option value="desc">↓ Desc</option>
            </select>
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap gap-2">
            {filters?.search && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Recherche: "{filters?.search}"
              </span>
            )}
            {filters?.commune && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Commune: {communes?.find(c => c?.id === filters?.commune)?.name}
              </span>
            )}
            {filters?.status && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Statut: {statuses?.find(s => s?.value === filters?.status)?.label}
              </span>
            )}
            {filters?.type && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Type: {propertyTypes?.find(t => t?.value === filters?.type)?.label}
              </span>
            )}
            {(filters?.priceMin || filters?.priceMax) && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Prix: {filters?.priceMin || '0'} - {filters?.priceMax || '∞'} FCFA
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyFilters;